// ─── Token Link Tracking Utility ────────────────────────────────────────────
// Detects ?token=NAME on any page URL and records a click in the
// token_link_clicks table.  The token stays in localStorage/Redux for the
// whole session (no attribution window — it lasts until cleared).
//
// Also provides attributeTokenSignup() for recording account creations.
//
// Usage: call initTokenTracking() once on app load (in App.js useEffect).
// ────────────────────────────────────────────────────────────────────────────

import { supabase } from "../supabaseClient";
import { getVisitorId } from "./trackingCore";
import logger from "./logger";

const TOKEN_CLICK_TRACKED_KEY = "token_click_tracked"; // tracks whether we already recorded a click this session

/**
 * The token NAME the visitor arrived with, or null.
 *
 * This is the single source of the token for every attribution caller: the name
 * is what the browser legitimately holds (set by the changeToken redux flow).
 * Callers pass the NAME, never an id, so the server does the name -> id
 * resolution and a client cannot credit a token it did not arrive on.
 *
 * @returns {string|null}
 */
export const getTokenName = () => {
  try {
    return localStorage.getItem("token") || null;
  } catch {
    return null; // localStorage can be unavailable (private mode / blocked)
  }
};

/**
 * Record a click in the token_link_clicks table.
 * @param {number} tokenId - The Token.id (not the name)
 * @param {string} pagePath - URL path the user landed on
 */
const trackTokenClick = async (tokenId, pagePath) => {
  try {
    const visitorId = getVisitorId();

    await supabase.from("token_link_clicks").insert({
      token_id: tokenId,
      visitor_id: visitorId,
      page_path: pagePath,
      referrer: document.referrer || null,
      user_agent: navigator.userAgent || null,
    });
  } catch (err) {
    logger.error("[TokenTracking] Failed to record click", err);
  }
};

/**
 * Initialise token link-click tracking. Should be called once on app load.
 *
 * 1. Reads ?token=NAME from the current URL
 * 2. Validates the token exists and is active in the Token table
 * 3. Records a click (only once per page-load to avoid duplicates on re-renders)
 *
 * NOTE: We do NOT remove ?token= from the URL — the existing system needs it
 * to stay so the token gets picked up by Redux (changeToken flow).
 */
export const initTokenTracking = async () => {
  try {
    const url = new URL(window.location.href);
    const tokenName = url.searchParams.get("token");

    if (!tokenName) return; // no token param — nothing to do

    // Avoid duplicate click tracking on the same page load
    const alreadyTracked = sessionStorage.getItem(TOKEN_CLICK_TRACKED_KEY);
    if (alreadyTracked === tokenName) return;

    // Look up the token by name
    const { data: token, error } = await supabase
      .from("Token")
      .select("id, name")
      .eq("name", tokenName)
      .eq("active", true)
      .maybeSingle();

    if (error || !token) {
      // Invalid or inactive token — ignore silently
      return;
    }

    // Record the click
    await trackTokenClick(token.id, url.pathname);

    // Mark as tracked for this session (so page refreshes don't double-count)
    sessionStorage.setItem(TOKEN_CLICK_TRACKED_KEY, tokenName);
  } catch (err) {
    logger.error("[TokenTracking] init error", err);
  }
};

/**
 * Attribute an account creation to the current token.
 * Call this after a successful signup/account-creation when a token is present.
 *
 * @param {string} userId - The auth user UUID
 */
export const attributeTokenSignup = async (userId) => {
  try {
    // Read the token from localStorage (set by changeToken in Redux)
    const tokenName = localStorage.getItem("token");
    if (!tokenName) return;

    await supabase.rpc("attribute_token_signup", {
      p_token_name: tokenName,
      p_user_id: userId,
    });
  } catch (err) {
    logger.error("[TokenTracking] Failed to attribute signup", err);
  }
};
